/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export const capitalizeWords = (str: string) => {
  return str.split(' ').map(capitalize).join(' ');
};

/**
 * Formats a value to be displayed in a readable format
 * @param value - The value to format
 * @returns The formatted value
 */
export const formatStringToReadableValue = (value: string | null): string => {
  if (!value || value === '-') return '-';
  
  if (value.includes('\n') || value.includes('{') || value.includes('[')) {
    try {
      if (value.includes('{') || value.includes('[')) {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      }
      
      return value
        .split('\n')
        .map((line, index) => {
          const trimmed = line.trim();
          if (trimmed === '') return '';
          
          const indentLevel = (line.match(/^\s*/)?.[0]?.length || 0) / 2;
          const indent = '  '.repeat(Math.max(0, indentLevel));
          
          return `${indent}${trimmed}`;
        })
        .filter(line => line !== '')
        .join('\n');
    } catch {
      return value
        .split('\n')
        .map(line => line.trim())
        .filter(line => line !== '')
        .join('\n');
    }
  }
  
  return value;
};

/**
 * Ordena: primeiro entradas cujo primeiro caractere visível não é dígito (faixa “alfabética”),
 * depois as que começam com dígito (faixa “numérica”). Dentro de cada faixa, ordem com número
 * tratado numericamente (ex.: BLOCO 2 antes de BLOCO 10) e ignorando diferença de maiúsculas.
 */
export function compareAlphaFirstThenNumericNames(a: string, b: string): number {
  const ta = (a ?? "").trim();
  const tb = (b ?? "").trim();
  const aDigitFirst = /^\d/.test(ta);
  const bDigitFirst = /^\d/.test(tb);
  if (aDigitFirst !== bDigitFirst) {
    return aDigitFirst ? 1 : -1;
  }
  return ta.localeCompare(tb, "pt", { numeric: true, sensitivity: "base" });
}

/**
 * Formats a string to show only numbers, remove letters and special characters
 * @param str - The string to format
 * @returns The formatted string with only numbers
 */
export const formatStringToNumbersOnly = (str: string): string => {
  return str.replace(/\D/g, '');
};

/**
 * Formats a currency string to a number
 * @param str - The string to format
 * @returns The formatted number
 */
export const formatCurrencyToNumber = (str: string): number => {
  return Number(
    str.replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.'),
  ) || 0;
};

export const formatNumberToCurrency = (num: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
};

export const stringToNumber = (str: string): number => {
  return Number(str.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'));
};
